import CallHistory from './CallHistory';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { VideoMesh } from './rtc';
import { readCall, saveCall } from './api';
import './video-support.css';

export function VideoTile({ stream, title, muted = false, screen = false, status, cameraOff = false }) {
  const video = useRef(null);
  const [needsPlay, setNeedsPlay] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    const el = video.current;
    if (!el) return;
    let disposed = false;
    setPlaying(false); setNeedsPlay(false);
    el.srcObject = stream || null;
    if (!stream) return;
    const play = () => {
      if (disposed) return;
      const result = el.play();
      if (result) result.catch(error => {
        if (!disposed && error.name === 'NotAllowedError') setNeedsPlay(true);
      });
    };
    const ready = () => { if (!disposed) { setPlaying(true); setNeedsPlay(false); } };
    const tracks = stream.getTracks();
    tracks.forEach(track => track.addEventListener('unmute', play));
    el.addEventListener('loadedmetadata', play);
    el.addEventListener('playing', ready);
    play();
    return () => {
      disposed = true;
      tracks.forEach(track => track.removeEventListener('unmute', play));
      el.removeEventListener('loadedmetadata', play);
      el.removeEventListener('playing', ready);
      el.srcObject = null;
    };
  }, [stream]);
  useEffect(() => {
    if (!expanded) return;
    const onKey = event => { if (event.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);
  return <div className={`vs-tile ${screen ? 'vs-tile--screen' : ''} ${expanded ? 'vs-tile--expanded' : ''}`}>
    <video ref={video} autoPlay playsInline muted={muted} aria-label={title} className={cameraOff ? 'vs-video-hidden' : ''} />
    {(!stream || !playing || cameraOff) && <div className="vs-placeholder"><span>{screen ? '▣' : title.slice(0, 1).toUpperCase()}</span><small>{cameraOff ? 'Camera off' : screen ? 'Waiting for shared screen…' : 'Connecting video…'}</small></div>}
    {needsPlay && <button className="vs-play" onClick={() => video.current.play().then(() => setNeedsPlay(false)).catch(() => {})}>Play video / audio</button>}
    {screen && <button className="vs-expand" aria-label={expanded ? 'Close expanded screen' : 'Expand shared screen'} onClick={() => setExpanded(!expanded)}>{expanded ? 'Close' : 'Expand'}</button>}
    <div className="vs-tile-label"><span>{title}</span><small>{status}</small></div>
  </div>;
}

export function VideoNotifications({ api }) {
  const [requests, setRequests] = useState([]);
  useEffect(() => {
    let stopped = false;
    let timer;
    const poll = async () => {
      try { const data = await api('/requests'); if (!stopped) setRequests(data.filter(c => c.status === 'waiting')); }
      catch (_) { if (!stopped) setRequests([]); }
      if (!stopped) timer = setTimeout(poll, 5000);
    };
    poll();
    return () => { stopped = true; clearTimeout(timer); };
  }, [api]);
  const notificationLabel = requests.length ? `${requests.length} incoming video calls` : 'Video calls';
  return <a className={`vs-notification ${requests.length ? 'vs-notification--incoming' : ''}`} href="/video-support" aria-label={notificationLabel} title={notificationLabel} aria-live="polite">
    <svg className="vs-notification-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><rect x="3" y="6" width="12" height="12" rx="3" /><path d="m15 10 6-3v10l-6-3" /></svg>
    <span className="vs-notification-label">Video calls</span>
    {requests.length > 0 && <span className="vs-notification-count">{requests.length}</span>}
  </a>;
}

export default function VideoSupport({ api, admin = false, inviteBaseUrl, signInUrl = '/account/login' }) {
  const [session, setSession] = useState(null);
  const [room, setRoom] = useState(null);
  const [requests, setRequests] = useState([]);
  const [invite, setInvite] = useState(null);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState(null);
  const [screen, setScreen] = useState(null);
  const [focusedScreen, setFocusedScreen] = useState('');
  const [previewScreen, setPreviewScreen] = useState(false);
  const [remotes, setRemotes] = useState({});
  const [connections, setConnections] = useState({});
  const [mic, setMic] = useState(true);
  const [camera, setCamera] = useState(true);
  const [shareLink, setShareLink] = useState('');
  const [notice, setNotice] = useState('');
  const [authRequired, setAuthRequired] = useState(false);
  const mesh = useRef(null);
  const screenRef = useRef(null);
  const alive = useRef(true);
  const operation = useRef(0);
  const signalQueue = useRef(Promise.resolve());

  const cleanup = useCallback(() => {
    operation.current += 1;
    if (mesh.current) mesh.current.close();
    mesh.current = null;
    if (screenRef.current) screenRef.current.getTracks().forEach(t => { t.onended = null; t.stop(); });
    screenRef.current = null;
    setLocal(null); setScreen(null); setRemotes({}); setConnections({});
  }, []);
  useEffect(() => {
    alive.current = true;
    const fragment = new URLSearchParams(window.location.hash.slice(1)).get('invite');
    const callId = new URLSearchParams(window.location.search).get('call');
    if (fragment && /^[a-f0-9]{24}\.[a-f0-9]{64}$/i.test(fragment)) {
      const [id, token] = fragment.split('.');
      const saved = readCall(id);
      if (saved) setSession(saved); else setInvite({ id, token });
    } else if (callId) setSession(readCall(callId));
    return () => { alive.current = false; operation.current += 1; if (mesh.current) mesh.current.close(); if (screenRef.current) screenRef.current.getTracks().forEach(t => { t.onended = null; t.stop(); }); };
  }, []);

  const run = async fn => {
    setBusy(true); setError('');
    try { await fn(); } catch (e) { if (alive.current) { setError(e.message); if (e.status === 401) setAuthRequired(true); } }
    finally { if (alive.current) setBusy(false); }
  };
  const enter = result => {
    saveCall(result); setRoom(result); setSession({ id: result.id, token: result.token });
    window.history.replaceState(null, '', `${window.location.pathname}?call=${result.id}`);
  };
  const roomApi = (path, body) => api(`/${session.id}${path}`, body, session.token);

  useEffect(() => {
    if (!admin || session) return;
    let stopped = false; let timer;
    const poll = async () => {
      try { const result = await api('/requests'); if (!stopped) { setRequests(result); setError(''); } }
      catch (e) { if (!stopped) setError(e.message); }
      if (!stopped) timer = setTimeout(poll, 5000);
    };
    poll(); return () => { stopped = true; clearTimeout(timer); };
  }, [admin, session, api]);

  useEffect(() => {
    if (!session) return;
    let stopped = false; let timer; let acks = []; const seen = new Set();
    const poll = async () => {
      try {
        const next = await api(`/${session.id}/sync`, { acks }, session.token);
        if (stopped) return;
        acks = []; setRoom(next);
        if (['ended', 'declined'].includes(next.status)) { cleanup(); return; }
        if (mesh.current) {
          mesh.current.sync(next.participants);
          for (const message of next.messages) {
            if (!seen.has(message.id)) {
              if (next.participants.some(p => p.id === message.from)) {
                try { await mesh.current.receive(message.from, message.payload); }
                catch (_) { if (!stopped) setError('A connection could not be established. Check your network or rejoin the call.'); }
              }
              seen.add(message.id);
            }
            acks.push(message.id);
          }
        }
      } catch (e) {
        if (stopped) return;
        setError(e.message);
        if ([403, 404].includes(e.status)) { cleanup(); setRoom(prev => ({ ...prev, status: 'ended' })); return; }
      }
      if (!stopped) timer = setTimeout(poll, 1000);
    };
    poll(); return () => { stopped = true; clearTimeout(timer); };
  }, [api, session, cleanup]);

  const joinMedia = async () => {
    const attempt = ++operation.current;
    if (!navigator.mediaDevices?.getUserMedia || !window.RTCPeerConnection) throw new Error('Video calls require a supported browser on HTTPS or localhost.');
    const config = await roomApi('/ice');
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: { width: { ideal: 640 }, height: { ideal: 360 } } });
    if (!alive.current || attempt !== operation.current) { stream.getTracks().forEach(t => t.stop()); return; }
    setMic(true); setCamera(true); setLocal(stream);
    mesh.current = new VideoMesh({ selfId: room.self.id, stream, iceServers: config.iceServers,
      send: (to, payload) => {
        const result = signalQueue.current.catch(() => {}).then(() => roomApi('/signals', { to, payload }));
        signalQueue.current = result; return result;
      },
      onRemote: (id, value) => { if (alive.current) setRemotes(prev => ({ ...prev, [id]: value })); },
      onConnection: (id, state) => { if (alive.current) setConnections(prev => ({ ...prev, [id]: state })); },
      onError: () => { if (alive.current && mesh.current) setError('Video connection interrupted. Check your network or rejoin the call.'); },
    });
    mesh.current.sync(room.participants);
    await roomApi('/media', { mic: true, camera: true, screen: false });
  };
  const toggleTrack = async kind => {
    const tracks = kind === 'mic' ? local.getAudioTracks() : local.getVideoTracks();
    const enabled = kind === 'mic' ? !mic : !camera;
    tracks.forEach(t => { t.enabled = enabled; });
    if (kind === 'mic') setMic(enabled); else setCamera(enabled);
    await roomApi('/media', { [kind]: enabled });
  };
  const stopScreen = async () => {
    const previous = screenRef.current;
    screenRef.current = null;
    if (previous) previous.getTracks().forEach(t => { t.onended = null; t.stop(); });
    if (mesh.current) await mesh.current.share(null);
    if (alive.current) { setScreen(null); setPreviewScreen(false); }
    await roomApi('/media', { screen: false });
  };
  const startScreen = async () => {
    if (screenRef.current) return stopScreen();
    if (!navigator.mediaDevices?.getDisplayMedia) throw new Error('Screen sharing is not supported in this browser. Try a desktop browser.');
    const currentMesh = mesh.current;
    const display = await navigator.mediaDevices.getDisplayMedia({ video: { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 15, max: 30 } }, audio: false });
    if (!alive.current || !currentMesh || currentMesh !== mesh.current) { display.getTracks().forEach(t => t.stop()); return; }
    try {
      await currentMesh.share(display.getVideoTracks()[0]);
      screenRef.current = display; setScreen(display);
      display.getVideoTracks()[0].onended = () => stopScreen().catch(e => setError(e.message));
      await roomApi('/media', { screen: true });
    } catch (e) {
      display.getTracks().forEach(t => { t.onended = null; t.stop(); });
      screenRef.current = null; setScreen(null);
      if (mesh.current === currentMesh) await currentMesh.share(null).catch(() => {});
      throw e;
    }
  };
  const leave = async end => {
    // Always release devices even if the server cannot be reached.
    cleanup();
    try { await roomApi(end ? '/end' : '/leave', {}); }
    finally { sessionStorage.removeItem(`video-support:${session.id}`); setRoom(prev => ({ ...prev, status: 'ended' })); }
  };
  const createInvite = async () => {
    const result = await roomApi('/invite', {});
    const link = `${(inviteBaseUrl || window.location.origin).replace(/\/$/, '')}/video-support#invite=${session.id}.${result.invite}`;
    setShareLink(link); setNotice('This link replaces previous invitations. Guests need host approval.');
    try { await navigator.clipboard.writeText(link); setNotice('Invitation copied. Guests need host approval.'); } catch (_) { /* A selectable link remains available. */ }
  };

  const closed = room && ['ended', 'declined'].includes(room.status);
  const remotePeople = (room?.participants || []).filter(p => p.id !== room?.self?.id);
  const presentations = remotePeople.filter(p => p.screen).map(p => ({ id: p.id, name: p.name, stream: remotes[p.id]?.screen, own: false }));
  if (screen) presentations.push({ id: 'local-screen', name: 'Your screen', stream: screen, own: true });
  const presentation = presentations.find(p => p.id === focusedScreen) || presentations[0];
  const cameraTiles = <><VideoTile stream={local} title="You" muted cameraOff={!camera} status={mic ? 'Microphone on' : 'Muted'} />{remotePeople.map(p => <VideoTile key={p.id} stream={remotes[p.id]?.camera} title={p.name} cameraOff={p.camera === false} status={connections[p.id] === 'connected' ? (p.mic === false ? 'Muted' : 'Connected') : connections[p.id] || 'Connecting…'} />)}</>;
  const ready = room?.status === 'active' && room?.self?.status === 'joined';
  return <main className={`vs-module ${local ? 'vs-module--live' : ''}`}>
    <header className="vs-heading"><div><span className="vs-eyebrow">MY UAE STARTUP · VIDEO SUPPORT</span><h1>{local ? 'Your video consultation' : admin ? 'Video call desk' : 'Let’s talk, face to face.'}</h1><p>{local ? 'Talk, share your screen, and work through the details together.' : admin ? 'Manage incoming calls and connect with customers.' : 'A little guidance can make a big difference. Connect with our team.'}</p></div><a className="vs-back" href="/">Back to home</a></header>
    {error && <div className="vs-error" role="alert">{error}{authRequired && <a href={signInUrl}> Sign in</a>}</div>}
    {notice && <p className="vs-notice" role="status">{notice}</p>}
    {!session && !admin && <section className="vs-welcome"><div className="vs-welcome-icon" aria-hidden="true">◉</div><span className="vs-eyebrow">PERSONAL SUPPORT, ZERO COST</span><h2>{invite ? 'You’re invited to a video call' : 'Your next step starts with a conversation.'}</h2><p>{invite ? 'Enter your name. The host will approve your request before you join.' : 'Request a free video call. Our team will accept your request as soon as someone is available.'}</p>{invite && <label className="vs-name">Your name<input value={name} maxLength={80} onChange={e => setName(e.target.value)} placeholder="Enter your name" /></label>}<button className="vs-primary" disabled={busy || (invite && name.trim().length < 2)} onClick={() => run(async () => enter(invite ? await api(`/${invite.id}/guest`, { name, invite: invite.token }) : await api('/requests', {})))}>{busy ? 'Connecting…' : invite ? 'Ask to join' : 'Free Video Call'}</button><div className="vs-features"><span>◉ Live video</span><span>▣ Two-way screen sharing</span><span>＋ Invite others</span></div><small>Camera and microphone are enabled only when you choose to join. Up to 6 participants.</small></section>}
    {!session && admin && <section className="vs-queue"><h2>Incoming requests <span>{requests.filter(c => c.status === 'waiting').length}</span></h2><p>Requests refresh automatically. Accept a customer to open a private call.</p>{requests.length === 0 && <div className="vs-empty">No calls right now. New requests will appear here.</div>}{requests.map(call => <article key={call.id}><div><strong>{call.name}</strong><small>{call.status === 'waiting' ? 'Waiting for support' : 'Call in progress'} · {new Date(call.createdAt).toLocaleTimeString()}</small></div><div className="vs-actions">{call.status === 'waiting' ? <><button disabled={busy} className="vs-primary" onClick={() => run(async () => enter(await api(`/${call.id}/accept`, {})))}>Accept call</button><button disabled={busy} onClick={() => run(async () => { await api(`/${call.id}/decline`, {}); setRequests(prev => prev.filter(c => c.id !== call.id)); })}>Decline</button></> : call.assignedToMe ? <button disabled={busy} onClick={() => run(async () => enter(await api(`/${call.id}/resume`, {})))}>Resume call</button> : <span>With another administrator</span>}</div></article>)}</section>}
    {session && !room && <section className="vs-welcome" role="status">Connecting to your call…</section>}
    {session && room && !closed && !local && <section className="vs-welcome"><div className="vs-welcome-icon" aria-hidden="true">◉</div><h2>{ready ? 'Your call is ready' : room.self?.role === 'guest' ? 'Waiting for the host' : 'We’ve notified our team'}</h2><p>{ready ? 'Join when you’re ready. Your browser will ask for camera and microphone access.' : 'Keep this page open. This screen will update automatically when your request is accepted.'}</p>{ready && <button disabled={busy} className="vs-primary" onClick={() => run(joinMedia)}>Join video call</button>}<button disabled={busy} className="vs-secondary" onClick={() => run(() => leave(room.self?.role !== 'guest'))}>Cancel / leave</button></section>}
    {local && room && <section className="vs-call-panel">
      <div className="vs-room-bar"><div><span className="vs-live-dot" />Live support session</div><span>{room.participants.length} / 6 participants</span><button disabled={busy} onClick={() => run(async () => { cleanup(); await joinMedia(); })}>Reconnect</button></div>
      <div className={`vs-workspace ${presentation ? 'vs-workspace--presenting' : ''}`}>
        <div className="vs-stage">
          {presentation ? <>
            <div className="vs-presentation-bar"><span>▣ {presentation.own ? 'You are presenting' : `${presentation.name} is presenting`}</span>{presentations.length > 1 && <label>View screen<select aria-label="Choose shared screen" value={presentation.id} onChange={e => { setFocusedScreen(e.target.value); setPreviewScreen(false); }}>{presentations.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>}</div>
            {presentation.own && !previewScreen ? <div className="vs-presenting-placeholder"><div>▣</div><h2>You’re sharing your screen</h2><p>Everyone in the call can see the window or screen you selected.</p><button onClick={() => setPreviewScreen(true)}>Preview shared screen</button><small>Preview is hidden to avoid the mirror effect when sharing this window.</small></div> : <VideoTile stream={presentation.stream} title={presentation.own ? 'Your shared screen' : `${presentation.name} · shared screen`} muted screen />}
          </> : <div className="vs-gallery">{cameraTiles}</div>}
        </div>
        <aside className="vs-participants"><div className="vs-participants-heading"><h2>In this call</h2><span>{room.participants.length}</span></div>{presentation ? <div className="vs-participant-videos">{cameraTiles}</div> : <div className="vs-people-list"><div><span className="vs-avatar">Y</span><div><strong>You</strong><small>{mic ? 'Microphone on' : 'Muted'}</small></div></div>{remotePeople.map(p => <div key={p.id}><span className="vs-avatar">{p.name.slice(0, 1)}</span><div><strong>{p.name}</strong><small>{connections[p.id] === 'connected' ? 'Connected' : 'Connecting…'}</small></div></div>)}<p className="vs-call-tip">Share a window or your screen to walk through things together.</p></div>}</aside>
      </div>
      <div className="vs-controls"><button aria-pressed={!mic} disabled={busy} onClick={() => run(() => toggleTrack('mic'))}>{mic ? 'Mute mic' : 'Unmute mic'}</button><button aria-pressed={!camera} disabled={busy} onClick={() => run(() => toggleTrack('camera'))}>{camera ? 'Camera off' : 'Camera on'}</button><button className="vs-share-control" aria-pressed={!!screen} disabled={busy} onClick={() => run(startScreen)}>{screen ? 'Stop sharing' : 'Share screen'}</button>{room.self.role !== 'guest' && <button disabled={busy} onClick={() => run(createInvite)}>Invite people</button>}<span className="vs-controls-divider" /><button className="vs-danger" disabled={busy} onClick={() => run(() => leave(false))}>Leave call</button>{room.self.role !== 'guest' && <button className="vs-end-all" disabled={busy} onClick={() => run(() => leave(true))}>End for everyone</button>}</div>
    </section>}
    {shareLink && !closed && <label className="vs-link">Call invitation<input readOnly value={shareLink} onFocus={e => e.target.select()} /><small>Anyone with this link can request admission until the call ends or the invitation is replaced.</small></label>}
    {room?.self?.role === 'admin' && !closed && room.waiting?.length > 0 && <section className="vs-lobby"><h2>Waiting to join</h2>{room.waiting.map(p => <div key={p.id}><span>{p.name}</span><button disabled={busy} onClick={() => run(() => roomApi('/admit', { participantId: p.id, allow: true }))}>Admit</button><button disabled={busy} onClick={() => run(() => roomApi('/admit', { participantId: p.id, allow: false }))}>Decline</button></div>)}</section>}
    {closed && <section className="vs-welcome"><h2>{room.status === 'declined' ? 'Our team couldn’t take this call' : 'This call has ended'}</h2><p>{room.status === 'declined' ? 'Please try again in a few minutes.' : 'Thank you for connecting with us.'}</p><a className="vs-primary" href="/video-support">{admin ? 'Back to call desk' : 'Start a new request'}</a></section>}
    {admin && !local && <CallHistory api={api} />}
  </main>;
}
